import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activateSubscription,
  cancelSubscription,
  createAccount,
  fetchCurrentUser,
  loginExistingAccount,
  pauseSubscription,
  reactivateSubscription,
} from './user.api';
import { SignupCredentials } from '../../models/user.types';
import { useAuth } from '../../contexts/AuthContext';

export const useCurrentUserQuery = () => {
  const { isLoggedIn } = useAuth();
  return useQuery({
    queryKey: ['currentUser', isLoggedIn],
    queryFn: () => fetchCurrentUser(isLoggedIn),
  });
};

export const useCurrentUserQueryOnLoggedOut = (isLoggedIn) => useQuery({
  queryFn: () => fetchCurrentUser(isLoggedIn),
});

export const clearCache = (queryClient, cartIds: number[], menuId?:number) => {
  queryClient.invalidateQueries({ queryKey: ['currentUser'] });
  queryClient.invalidateQueries({ queryKey: ['menus'] });
  if (menuId) queryClient.invalidateQueries({ queryKey: ['menuItems', menuId, true] });
  cartIds.forEach((cartId: number) => {
    queryClient.invalidateQueries({ queryKey: ['cartItems', cartId] });
  });
};

export const useReactivateSubscriptionMutation = (cartIds: number[]) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => reactivateSubscription(),
    onSuccess: () => {
      clearCache(queryClient, cartIds);
    },
  });
};

export const usePauseSubscriptionMutation = (activateAfterWeeks: number, cartIds: number[]) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => pauseSubscription(activateAfterWeeks),
    onSuccess: () => clearCache(queryClient, cartIds),
  });
};

export const useCancelSubscriptionMutation = (reason: string, reasonDetails: string,
  preparedFollowUpOptIn: boolean, cartIds: number[]) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => cancelSubscription(reason, reasonDetails, preparedFollowUpOptIn),
    onSuccess: () => clearCache(queryClient, cartIds),
  });
};

export const useCreateAccountMutation = () => useMutation({
  mutationFn: async (credentials: SignupCredentials) => createAccount(credentials),
  onSuccess: (signupStatus) => signupStatus,
});

export const useActivateSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (couponCode?: string) => activateSubscription(couponCode),
    onSuccess: () => {
      clearCache(queryClient, []);
    },
  });
};

export const useLoginAccountMutation = () => useMutation({
  mutationFn: async (formData: FormData) => loginExistingAccount(formData),
  onSuccess: (signInStatus) => signInStatus,
});
